/**
 * useAuth — 4-mode auth for Eval Pilot. The Worker's requireAuth accepts THREE
 * token kinds directly as the Bearer (no idToken→JWT exchange):
 *   - eval-pilot JWT  (password login → POST /api/v1/auth/login)
 *   - PayPilot suite JWT (cross-app #token= handoff from the launcher)
 *   - LINE idToken    (LIFF in-app — used DIRECTLY as the Bearer)
 *
 * Boot precedence: #token handoff → dev/mock → persisted password|suite jwt →
 * LINE idToken → web password login. GET /me is special-cased (bare envelope)
 * via fetchMe. Errors: 404→unpaired(onboarding), 402→needs-activation, 401→relogin.
 *
 * status lives in the store (shared across every useAuth caller); boot runs once
 * via a module-level promise.
 */
import { useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiError, fetchMe, setUnauthorizedHandler, setProductInactiveHandler } from '../lib/api-client';
import { useAuthStore } from '../lib/store';
import { getIDToken, isMock, isInClient, login as liffLogin, logout as liffLogout } from '../lib/liff';
import type { AuthMode, AuthProfile, AuthTenant, AuthUser } from '@humanai/shared';

/** Dev/mock profile (VITE_LIFF_ID empty) so the shell renders without a backend. */
const MOCK_PROFILE: AuthProfile = {
  user: { id: 'u_mock', name: 'ผู้ดูแลระบบ (ทดสอบ)', role: 'OWNER' },
  tenant: { id: 't_mock', name: 'องค์กรทดสอบ' },
};

function toProfile(me: { user: AuthUser; tenant: AuthTenant | null }): AuthProfile {
  return { user: me.user, tenant: me.tenant ?? { id: '', name: '—' } };
}

/** Pull `#token=…` out of the URL once (cross-app handoff). Rewrites the hash so
 *  the JWT doesn't linger — done before the router reads the URL. */
function consumeTokenHash(): string | null {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash;
  if (!hash.startsWith('#token=')) return null;
  const token = decodeURIComponent(hash.slice('#token='.length));
  if (!token) return null;
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  return token;
}

async function bootstrap(force = false): Promise<void> {
  const { setAuth, setStatus, clear } = useAuthStore.getState();

  // 1. Cross-app #token= handoff (suite SSO).
  const handed = consumeTokenHash();
  if (handed) {
    try {
      setAuth({ jwt: handed, authMode: 'suite', profile: toProfile(await fetchMe(handed)) });
      setStatus('ok');
      return;
    } catch {
      /* expired/forged → fall through to normal bootstrap */
    }
  }

  // 2. Dev/mock — no LINE, no backend.
  if (isMock) {
    setAuth({ jwt: 'dev', authMode: 'dev', profile: MOCK_PROFILE });
    setStatus('ok');
    return;
  }

  // 3. Pick a token by mode. Password/suite reuse the persisted JWT; LINE always
  //    pulls a FRESH idToken (the persisted one is likely expired).
  const { jwt, authMode } = useAuthStore.getState();
  let token: string | null = null;
  let mode: AuthMode = 'line';
  if (!force && (authMode === 'password' || authMode === 'suite') && jwt) {
    token = jwt;
    mode = authMode;
  } else {
    const id = getIDToken();
    if (id) {
      token = id;
      mode = 'line';
    }
  }
  if (!token) {
    setStatus('no-liff');
    return;
  }

  // 4. Verify against /me.
  try {
    setAuth({ jwt: token, authMode: mode, profile: toProfile(await fetchMe(token)) });
    setStatus('ok');
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 404 || err.code === 'USER_NOT_FOUND') return setStatus('unpaired');
      if (err.status === 402 || err.code === 'PRODUCT_NOT_ACTIVE') return setStatus('needs-activation');
      if (err.status === 401) {
        if (mode === 'line' && isInClient()) {
          liffLogin(); // refresh idToken via LINE
          return;
        }
        clear();
        return setStatus('no-liff');
      }
    }
    setStatus('error', err instanceof Error ? err.message : 'Auth failed');
  }
}

/** Boot exactly once across all useAuth callers. */
let bootPromise: Promise<void> | null = null;
function ensureBoot(): void {
  if (!bootPromise) bootPromise = bootstrap(false);
}

/** Web password login (Owner/HR). Sets the session on success. */
export async function loginWithPassword(email: string, password: string): Promise<void> {
  const res = await apiFetch<{ token: string }>('/api/v1/auth/login', {
    method: 'POST',
    body: { email, password },
    skipAuth: true,
  });
  useAuthStore.getState().setAuth({
    jwt: res.token,
    authMode: 'password',
    profile: toProfile(await fetchMe(res.token)),
  });
  useAuthStore.getState().setStatus('ok');
}

export function useAuth() {
  const profile = useAuthStore((s) => s.profile);
  const jwt = useAuthStore((s) => s.jwt);
  const status = useAuthStore((s) => s.status);
  const error = useAuthStore((s) => s.error);
  const clear = useAuthStore((s) => s.clear);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      useAuthStore.getState().clear();
      useAuthStore.getState().setStatus('no-liff');
    });
    setProductInactiveHandler(() => useAuthStore.getState().setStatus('needs-activation'));
    ensureBoot();
  }, []);

  const signOut = useCallback(() => {
    const mode = useAuthStore.getState().authMode;
    clear();
    if (mode === 'line') {
      try {
        liffLogout();
      } catch {
        /* ignore */
      }
    }
    useAuthStore.getState().setStatus('no-liff');
  }, [clear]);

  const refreshAuth = useCallback((force = true) => {
    bootPromise = bootstrap(force);
    return bootPromise;
  }, []);

  return {
    profile,
    jwt,
    status,
    error,
    role: profile?.user.role,
    tenant: profile?.tenant,
    loginWithPassword,
    signOut,
    logout: signOut,
    refreshAuth,
  };
}

// ===== ตัวสลับบริษัท (tenant switcher) =====
export interface TenantMembership {
  user_id: string;
  tenant_id: string;
  tenant_code: string | null;
  tenant_slug: string;
  tenant_name: string;
  tenant_status: string;
  role: string;
  is_current: boolean;
}

export function useMyTenants() {
  return useQuery({
    queryKey: ['me', 'tenants'],
    queryFn: () => apiFetch<{ memberships: TenantMembership[] }>('/api/v1/me/tenants').then((d) => d.memberships),
    staleTime: 5 * 60_000,
  });
}

export function useSwitchTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tenant_id: string) =>
      apiFetch<{ jwt: string; tenant_name?: string }>('/api/v1/me/switch-tenant', { method: 'POST', body: { tenant_id } }),
    onSuccess: async (data) => {
      // mint แล้วดึงโปรไฟล์บริษัทใหม่ผ่าน fetchMe (เหมือน loginWithPassword) → setAuth ใหม่
      useAuthStore.getState().setAuth({ jwt: data.jwt, authMode: 'password', profile: toProfile(await fetchMe(data.jwt)) });
      await qc.invalidateQueries(); // ล้าง cache ทั้งหมด → refetch ตามบริษัทใหม่
    },
  });
}
